'use client';
/**
 * RELIER UNE PERSONNE À UN COMPTE QUI EXISTE DÉJÀ — module à part, RQ seul.
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * ⚠️ CE FICHIER EXISTE POUR QU'IL N'Y EN AIT QU'UN (13/09/2026). Le composant
 * vivait dans l'écran des parcours d'intégration, seul endroit d'où le geste
 * était atteignable. La RQ a demandé qu'il le soit AUSSI depuis la fiche du
 * personnel — ce qui est la bonne demande : pour quelqu'un d'ancien, passer
 * par un dossier d'intégration obligeait à en OUVRIR un là où il n'y en avait
 * pas.
 *
 * Le recopier dans le second écran aurait donné deux fenêtres qui divergent au
 * premier avertissement ajouté — et celui-ci porte précisément l'avertissement
 * qu'il ne faut pas perdre. Il est donc SORTI, et importé aux deux endroits.
 */
import React from 'react';
import { useGed } from '@/lib/store';
import { fetchEcriture } from '@/lib/ecrire';
import { nomDe } from '@/lib/nom';
import { inputStyle, Selecteur, Modale as Fenetre } from '@/components/ui';
import type { RhPersonnel } from '@/lib/types';

import { Bouton } from '@/shared/Bouton';
import { fmtDateHeure } from '@/lib/meta';
/** Date lisible — locale, jamais l'ISO brut sous les yeux de quelqu'un. */
const jourLocal = (v?: string | null) => (v ? fmtDateHeure(v, false) : '');

type CompteLibre = {
  username: string; email: string | null; nom: string; profil: string;
  etat: string; derniereConnexion: string | null; creeLe: string | null;
};

/**
 * REVENDIQUER UN COMPTE QUI EXISTE DÉJÀ — volet ⑧, RQ seul.
 *
 * ⚠️ CE GESTE NE SE DÉFAIT PAS PROPREMENT, et c'est ce qui commande tout ce qui
 * suit. Ce qui a été fait sous un compte AVANT le rattachement reste attribué
 * au compte ; le journal chaîné, lui, portera désormais le nom de la personne.
 * Se tromper de compte, c'est donc écrire durablement les actes de quelqu'un
 * sous le nom d'un autre.
 *
 * ── Trois précautions, et chacune répond à une façon de se tromper ─────────
 *
 * ① ON NE PROPOSE QUE LES COMPTES QUE PERSONNE NE REVENDIQUE. Le moteur les
 *    filtre ; proposer un compte déjà rattaché inviterait à le DÉPLACER —
 *    c'est-à-dire à retirer ses accès à quelqu'un par un geste qui n'a l'air
 *    que d'un rattachement.
 *
 * ② ON MONTRE CE QUE LE COMPTE A DÉJÀ FAIT. « Jamais connecté » et « dernière
 *    connexion il y a trois jours » ne demandent pas la même attention : le
 *    second a une histoire, et c'est elle qu'on s'apprête à rebaptiser.
 *
 * ③ ON DEMANDE CONFIRMATION EN NOMMANT LES DEUX CÔTÉS. Une liste déroulante
 *    qui écrit au premier choix transforme un glissement de doigt en
 *    attribution d'accès.
 */
export function RattacherCompte({ personne, onFait, libelle }: {
  personne: RhPersonnel;
  onFait: () => void;
  /** Le libellé du bouton. La fiche du personnel a déjà une section « Compte
   *  de connexion » au-dessus : « Relier à un compte existant » y serait
   *  redondant, « Relier à un compte » suffit. Le titre de la fenêtre, lui, ne
   *  change pas — c'est là qu'on dit ce qu'on fait. */
  libelle?: string;
}) {
  const { showToast } = useGed();
  const [ouvert, setOuvert] = React.useState(false);
  const [libres, setLibres] = React.useState<CompteLibre[] | null>(null);
  const [erreur, setErreur] = React.useState('');
  const [choisi, setChoisi] = React.useState('');
  const [envoi, setEnvoi] = React.useState(false);

  React.useEffect(() => {
    if (!ouvert) return undefined;
    let vivant = true;
    fetch(`/api/ged/ged/rh/personnel/${encodeURIComponent(personne.orgId)}/comptes-libres`, { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error())))
      .then((d) => { if (vivant) setLibres(d.comptes || []); })
      /* ⚠️ `null` reste `null` : une liste vide se lirait « aucun compte libre »,
         ce qui est une AFFIRMATION. On dit qu'on n'a pas pu lire. */
      .catch(() => { if (vivant) setErreur('Les comptes n’ont pas pu être lus.'); });
    return () => { vivant = false; };
  }, [ouvert, personne.orgId]);

  const rattacher = async () => {
    if (!choisi) return;
    setEnvoi(true);
    try {
      const r = await fetchEcriture(`/api/ged/ged/rh/personnel/${encodeURIComponent(personne.orgId)}/rattacher-compte`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        credentials: 'include', body: JSON.stringify({ username: choisi }),
      });
      const d = await r.json().catch(() => null);
      if (!r.ok) { showToast(d?.error?.message || 'Rattachement refusé.', 'erreur'); return; }
      setOuvert(false); setChoisi('');
      onFait();
    } finally { setEnvoi(false); }
  };

  if (!ouvert) {
    return (
      <Bouton taille="sm" onClick={() => setOuvert(true)}>
        {libelle || 'Relier à un compte existant'}
      </Bouton>
    );
  }
  const c = (libres || []).find((x) => x.username === choisi);
  return (
    <Fenetre titre="Relier à un compte existant" largeur={620} onClose={() => setOuvert(false)}
      pied={<>
        <Bouton taille="sm" onClick={() => setOuvert(false)}>Annuler</Bouton>
        <Bouton variante="primaire" disabled={!choisi || envoi} onClick={() => void rattacher()}>
          {envoi ? 'Rattachement…' : 'Relier ce compte'}
        </Bouton>
      </>}>
      <div style={{ display: 'grid', gap: 12 }}>
        <p style={{ fontSize: 12.5, margin: 0, lineHeight: 1.55 }}>
          Relier <strong>{nomDe(personne)}</strong> à un compte qui existe déjà.{' '}
          {/* ⚠️ DIT AVANT LE GESTE, pas après : c'est ce qui rend le choix
              sérieux, et c'est la seule chose qu'on ne pourra pas reprendre. */}
          <span style={{ color: 'var(--amber-600)' }}>
            Ce que ce compte a fait <strong>avant</strong> ce rattachement restera au nom du compte ;
            ce qu’il fera ensuite sera attribué à cette personne.
          </span>
        </p>

        {!!erreur && <p style={{ fontSize: 12.5, color: 'var(--amber-600)', margin: 0 }}>{erreur}</p>}
        {!erreur && !libres && <p style={{ fontSize: 12.5, color: 'var(--text-muted)', margin: 0 }}>Lecture des comptes…</p>}
        {libres && !libres.length && (
          <p style={{ fontSize: 12.5, color: 'var(--text-muted)', margin: 0, lineHeight: 1.55 }}>
            Aucun compte libre : tous ceux qui existent sont déjà rattachés à quelqu’un. Si le compte
            de cette personne figure ailleurs, détachez-le d’abord de la fiche qui le porte.
          </p>
        )}
        {!!libres?.length && (
          <label style={{ fontSize: 12.5 }}>Compte à relier
            <Selecteur value={choisi} onChange={setChoisi} style={inputStyle}>
              <option value="">— choisir —</option>
              {libres.map((x) => (
                <option key={x.username} value={x.username}>
                  {x.username}{x.nom ? ` — ${x.nom}` : ''}{x.email ? ` · ${x.email}` : ''}
                </option>
              ))}
            </Selecteur>
          </label>
        )}

        {/* ⚠️ CE QUE LE COMPTE A DÉJÀ FAIT — « jamais connecté » et « dernière
            connexion il y a trois jours » ne demandent pas la même attention. */}
        {c && (
          <div style={{ display: 'grid', gap: 4, fontSize: 12.5, padding: '10px 12px',
            borderRadius: 'var(--radius-sm)', background: 'var(--surface-sunken)', border: '1px solid var(--border-default)' }}>
            <div><span style={{ color: 'var(--text-muted)' }}>Profil </span><strong>{c.profil || '—'}</strong></div>
            <div><span style={{ color: 'var(--text-muted)' }}>État </span><strong>{c.etat}</strong></div>
            <div>
              <span style={{ color: 'var(--text-muted)' }}>Dernière connexion </span>
              {c.derniereConnexion
                ? <strong>{jourLocal(c.derniereConnexion)}</strong>
                : <strong style={{ color: 'var(--amber-600)' }}>jamais connecté</strong>}
            </div>
            {!!c.creeLe && <div><span style={{ color: 'var(--text-muted)' }}>Créé le </span>{jourLocal(c.creeLe)}</div>}
          </div>
        )}
      </div>
    </Fenetre>
  );
}
